import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import './KustomCheckoutFrame.css';

/**
 * Renders the hosted-checkout snippet provided by the BE (Kustom / Bambora).
 * Uses Range.createContextualFragment so embedded <script> tags execute.
 *
 * Shows a spinner overlay while the payment provider loads or redirects — for
 * Bambora there are a few seconds between injecting the snippet and the actual
 * redirect, during which the container would otherwise be blank. The snippet is
 * injected into a dedicated inner slot so it never clobbers the React-rendered
 * spinner.
 *
 * Props:
 *   - snippet: HTML string returned from the checkout endpoint
 *   - onMessage: optional callback for postMessage events from the provider
 *   - loadingText: optional label under the spinner
 */
const KustomCheckoutFrame = ({ snippet, onMessage, loadingText = 'Sender dig til betaling…' }) => {
  const slotRef = useRef(null);
  const [frameLoading, setFrameLoading] = useState(true);

  useEffect(() => {
    const el = slotRef.current;
    if (!el || !snippet) return undefined;
    el.innerHTML = '';
    setFrameLoading(true);

    try {
      const range = document.createRange();
      range.selectNode(el);
      const fragment = range.createContextualFragment(snippet);
      el.appendChild(fragment);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[checkout] failed to inject payment snippet', err);
      el.innerHTML = snippet; // fallback (no <script> exec)
    }

    // Hide the spinner once the provider iframe has loaded. For redirect flows
    // there's no iframe — the spinner stays visible until the page navigates
    // away (a fallback timeout ensures it never sticks indefinitely).
    let done = false;
    const finish = () => { if (!done) { done = true; setFrameLoading(false); } };

    const tryAttach = () => {
      const iframe = el.querySelector('iframe');
      if (!iframe) return false;
      if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') finish();
      else iframe.addEventListener('load', finish, { once: true });
      return true;
    };

    let observer;
    if (!tryAttach()) {
      observer = new MutationObserver(() => { if (tryAttach()) observer.disconnect(); });
      observer.observe(el, { childList: true, subtree: true });
    }
    const fallback = setTimeout(finish, 20000);

    return () => {
      if (observer) observer.disconnect();
      clearTimeout(fallback);
    };
  }, [snippet]);

  useEffect(() => {
    if (!onMessage) return undefined;
    const handler = (e) => {
      // eslint-disable-next-line no-console
      console.log('[checkout] payment postMessage', e?.data);
      onMessage(e);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  return (
    <div className="kustom-checkout-frame">
      {frameLoading && (
        <div className="checkout-frame__loading">
          <Loader2 className="checkout-frame__spinner" />
          {loadingText && <span className="checkout-frame__loading-text typo-body-sm">{loadingText}</span>}
        </div>
      )}
      <div className="checkout-frame__slot" ref={slotRef} />
    </div>
  );
};

export default KustomCheckoutFrame;
